/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_PRESENTATIONS } from "../../InventoryControlPageService/graphql/getPresentations.query";
import { GET_SECTIONS } from "../graphql/getSections.query";
import { GET_COSTUME_TYPES } from "../graphql/getCostumeTypes.query";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IFilterOptionsMethods extends IPluginMethodMap {
  getFilterOptions(args: any): Promise<any>;
}

export class FilterOptionsService implements IAgentPlugin {
  readonly methods: IFilterOptionsMethods;

  constructor() {
    this.methods = {
      getFilterOptions: this.getFilterOptions.bind(this),
    };
  }

  async getFilterOptions(args: any = {}) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (!client) {
        return {
          sectionFilter: [],
          costumeTypeFilter: [],
        };
      }

      const presentationId = args?.presentation;

      if (!presentationId) {
        return {
          sectionFilter: [],
          costumeTypeFilter: [],
        };
      }

      const sectionsRes = await client.query({
        query: GET_SECTIONS,
        variables: {
          filter: {
            presentation: {
              eq: presentationId,
            },
          },
        },
        fetchPolicy: "network-only",
      });

      const costumeTypeFilter: {
        value: string;
        label: string;
      }[] = [];

      const costumeTypeVariables: any = {
        filter: {
          presentation: {
            eq: presentationId,
          },
        },
      };

      if (args?.section) {
        costumeTypeVariables.filter = {
          ...costumeTypeVariables.filter,
          section: {
            eq: args.section,
          },
        };
      }

      const costumeTypesRes = await client.query({
        query: GET_COSTUME_TYPES,
        variables: costumeTypeVariables,
        fetchPolicy: "network-only",
      });

      const sectionFilter =
        sectionsRes?.data?.getSections?.content?.map((item: any) => ({
          value: item?.id,
          label: item?.name,
        })) ?? [];

      const mappedCostumeTypes =
        costumeTypesRes?.data?.getCostumeTypes?.content?.map((item: any) => ({
          value: item?.id,
          label: item?.name,
        })) ?? [];

      return {
        sectionFilter,
        costumeTypeFilter: mappedCostumeTypes,
      };
    } catch (err: any) {
      console.log("error in filter options", err);

      return {
        sectionFilter: [],
        costumeTypeFilter: [],
      };
    }
  }
}
